/**
 * useAgreementProject.jsx — context for the active project's intake, plan and checklist.
 *
 * Source of truth is the backend. localStorage is a cache and an offline fallback so a founder
 * halfway through the intake does not lose their answers on a refresh or a dropped connection.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EMPTY_INTAKE, intakeCompleteness, missingRequired, intakeToTokens, intakeToProfile } from "@/data/agreements/intakeSchema";
import { EMPTY_CHECKLIST_ROW, checklistProgress } from "@/data/agreements/masterChecklist";
import { buildPlan } from "@/lib/agreements/recommend";
import { intakeApi, planApi, checklistApi, eventsApi } from "@/lib/agreements/api";

const STORAGE_PREFIX = "cpg-agreements";
const AgreementProjectContext = createContext(null);

const readCache = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};
const writeCache = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota or privacy mode — the backend is still the source of truth */
  }
};

export function AgreementProjectProvider({ children, projectId = "default" }) {
  const intakeKey = `${STORAGE_PREFIX}:intake:${projectId}`;
  const checklistKey = `${STORAGE_PREFIX}:checklist:${projectId}`;

  const [intake, setIntakeState] = useState(() => ({ ...EMPTY_INTAKE, ...readCache(intakeKey, {}) }));
  const [checklist, setChecklistState] = useState(() => readCache(checklistKey, {}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load from the backend; fall back to whatever the cache has.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([intakeApi.get(projectId), checklistApi.list(projectId)])
      .then(([intakeRes, checklistRes]) => {
        if (cancelled) return;
        if (intakeRes.status === "fulfilled" && intakeRes.value?.answers) {
          const next = { ...EMPTY_INTAKE, ...intakeRes.value.answers };
          setIntakeState(next);
          writeCache(intakeKey, next);
        }
        // The backend returns a { docId: row } map — the shape masterChecklist.js consumes.
        if (checklistRes.status === "fulfilled" && checklistRes.value && typeof checklistRes.value === "object") {
          const map = checklistRes.value.rows && !Array.isArray(checklistRes.value.rows)
            ? checklistRes.value.rows
            : checklistRes.value;
          if (map && typeof map === "object" && !Array.isArray(map)) {
            setChecklistState(map);
            writeCache(checklistKey, map);
          }
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, intakeKey, checklistKey]);

  const setIntake = useCallback(
    (patch) => {
      setIntakeState((prev) => {
        const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
        writeCache(intakeKey, next);
        return next;
      });
    },
    [intakeKey],
  );

  const saveIntake = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await intakeApi.save(projectId, { answers: intake });
      eventsApi.track({ type: "complete_intake", projectId });
      return saved;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setSaving(false);
    }
  }, [intake, projectId]);

  const setChecklistRow = useCallback(
    (docId, patch) => {
      setChecklistState((prev) => {
        const row = { ...EMPTY_CHECKLIST_ROW, ...(prev[docId] || {}), ...patch, docId };
        const next = { ...prev, [docId]: row };
        writeCache(checklistKey, next);
        checklistApi.upsert(projectId, docId, row).catch(() => {});
        eventsApi.track({ type: "update_checklist", projectId, docId });
        return next;
      });
    },
    [checklistKey, projectId],
  );

  // The plan is derived, not stored client-side: same answers always give the same plan.
  const plan = useMemo(() => buildPlan(intake), [intake]);

  const savePlan = useCallback(async () => {
    const payload = {
      counts: plan.counts,
      items: plan.items.map((i) => ({
        docId: i.docId,
        level: i.level,
        reasons: i.reasons,
        ruleIds: i.ruleIds,
      })),
    };
    const saved = await planApi.generate(projectId, payload);
    eventsApi.track({ type: "generate_plan", projectId });
    return saved;
  }, [plan, projectId]);

  const value = useMemo(
    () => ({
      projectId,
      intake,
      setIntake,
      saveIntake,
      completeness: intakeCompleteness(intake),
      missingRequired: missingRequired(intake),
      /** Keyed by token string — use to seed a form. */
      tokens: intakeToTokens(intake),
      /** Keyed by intake field id — this is what renderDocument() wants as its `profile`. */
      templateProfile: intakeToProfile(intake),
      plan,
      savePlan,
      checklist,
      setChecklistRow,
      progress: checklistProgress(checklist),
      loading,
      saving,
      error,
      hasIntake: intakeCompleteness(intake) > 0,
    }),
    [projectId, intake, setIntake, saveIntake, plan, savePlan, checklist, setChecklistRow, loading, saving, error],
  );

  return <AgreementProjectContext.Provider value={value}>{children}</AgreementProjectContext.Provider>;
}

export function useAgreementProject() {
  const ctx = useContext(AgreementProjectContext);
  if (!ctx) throw new Error("useAgreementProject must be used inside <AgreementProjectProvider>");
  return ctx;
}

export default useAgreementProject;
